import { Injectable } from "@angular/core";
import { Sort } from "@angular/material/sort";
import { ConfigurationService } from "@app/configuration/configuration.service";
import { LookupModel } from "@app/configuration/models/configuration.models";
import { PagedList } from "@app/core";
import { MonitoringListService } from '@app/monitoring/list/services/monitoring-list.service';
import { Monitoring } from '@app/monitoring/list/types/monitoring';
import { RootStoreState, ViewStateComponentStore, ViewStateComponentStoreState } from "@app/root-store";
import { CaseListFilterOptions } from "@app/shared/types/case-list-filter-options";
import { Pagination, Selected } from "@app/types/core";
import { PopoverFilterOption } from '@app/shared/filters';
import { tapResponse } from '@ngrx/component-store';
import { Store } from "@ngrx/store";
import { forkJoin, Observable } from "rxjs";
import { concatMap, switchMap, tap, withLatestFrom } from "rxjs/operators";
import { MonitoringListFilters } from "../types/monitoring-filters";
import { GetMonitoringListPayload } from "../types/monitoring-params";

export interface MonitoringListState extends ViewStateComponentStoreState {
   monitoringCases: Monitoring[];
   payload: GetMonitoringListPayload;
   options: CaseListFilterOptions;
   userRoles: LookupModel[];
   userRolesResponses: Response[];
   count: number;
   loading: boolean,
   toggle: boolean
}

export interface MonitoringListViewState {
   filters: MonitoringListFilters;
   selected: Selected;
   search: string;
   activeOnly: boolean;
   pagination: Pagination;
   sort: Sort;
}

const initialState: MonitoringListState = {
   monitoringCases: [],
   payload: {
      pagination: null,
      sort: null,
      filters: {},
      selected: {},
      search: null
   },
   options: {
      legalTeams: null,
      fundingPrograms: null,
      caseStatuses: null
   },
   userRoles: [],
   userRolesResponses: [],
   count: 0,
   loading: false,
   toggle: false,
   viewId: null
}

const initialViewState: MonitoringListViewState = {
   filters: {
      number: null,
      caseName: null,
      client: null,
      primaryStaff: null,
      legalTeam: null,
      funder: null,
      status: null,
      opened: null,
      closed: null
   },
   selected: {
      users: [],
      roles: []
   },
   search: null,
   activeOnly: true,
   pagination: { page: 1, pageSize: 25 },
   sort: { active: 'opened', direction: 'desc' }
};

@Injectable()
export class MonitoringStateStore extends ViewStateComponentStore<MonitoringListState, MonitoringListViewState> {

   constructor(
      rootStore: Store<RootStoreState.State>,
      private service: MonitoringListService,
      private configurationService: ConfigurationService) {
      super(rootStore, initialViewState);
   }

   init(viewId: string): void {
      this.initCore({
         ...initialState,
         viewId
      });
      this.patchState({ loading: true });
      this.service.getFilterOptions().pipe(
         tapResponse(options =>
            this.patchState({ options, loading: false }), error => console.log(error))).subscribe();
      forkJoin([
         this.service.getFilterOptions(),
         this.configurationService.getLookupType('involvement-user-role')]).subscribe(
            ([options, types]) => {
               this.patchState({ options, userRoles: types.lookups, loading: false }), error => console.log(error);
            })
   }

   readonly legalTeamOptions$: Observable<PopoverFilterOption[]> = this.select(
      this.state$,
      ({ options }) => options.legalTeams?.map(value => ({ value, text: value }))
   );

   readonly funderOptions$: Observable<PopoverFilterOption[]> = this.select(
      this.state$,
      ({ options }) => options.fundingPrograms?.map(value => ({ value, text: value }))
   );

   readonly statusOptions$: Observable<PopoverFilterOption[]> = this.select(
      this.state$,
      ({ options }) => options.caseStatuses?.map(value => ({ value, text: value }))
   );

   readonly vm$ = this.select(
      this.legalTeamOptions$,
      this.funderOptions$,
      this.statusOptions$,
      this.state$,
      this.viewState$,
      (legalTeamOptions, funderOptions, statusOptions, { monitoringCases, payload, userRoles, count, loading, toggle }, { filters, selected, activeOnly, pagination, sort }) => (
         { legalTeamOptions, funderOptions, statusOptions, monitoringCases, payload, userRoles, count, loading, toggle, filters, selected, activeOnly, pagination, sort })
   )

   readonly updateCases = this.updater((state, { list, count }: PagedList<Monitoring>): MonitoringListState => ({
      ...state,
      monitoringCases: list,
      count,
      loading: false
   }));

   readonly reload = this.effect((origin$: Observable<GetMonitoringListPayload>) => origin$.pipe(
      tap(payload => this.patchState({ payload, loading: true })),
      switchMap(payload => this.service.getMonitoringList(payload).pipe(
         tapResponse(({ count, page, pageSize, list }) => this.updateCases({ list, page, pageSize, count }), err => console.log(err))
      ))
   ))(this.viewState$);

   readonly addMonitoring = this.updater((state, monitoring: Monitoring) => ({
      ...state,
      monitoringCases: state.monitoringCases.filter(c => c.involvementId !== monitoring.involvementId).concat(monitoring)
   }));

   readonly deleteMonitoring = this.effect((origin$: Observable<string>) => origin$.pipe(
      tap(() => this.patchState({ loading: true })),
      concatMap(id => this.service.deleteMonitoring(id)),
      withLatestFrom(this.viewState$),
      concatMap(([_, payload]) => this.service.getMonitoringList(payload)),
      tapResponse(({ count, page, pageSize, list }) => this.updateCases({ list, page, pageSize, count }), err => console.log(err))
   ));
}